import type { Origin } from "@rally/ui-kit";
import type { Answering, PutAway, RecordedMessage, TargetCapabilities } from "../../net/api.ts";
import type { Reaction } from "../reaction/reacting.ts";
import { ALL_TARGET_CAPABILITIES, NOTHING_PUT_AWAY } from "../target-actions.ts";

export type Line = {
  kind: string;
  id: string;
  by?: string;
  text?: string;
  at: string;

  submission?: string | null;
  between?: boolean;

  reactions?: Reaction[];
  away?: PutAway;

  answering?: Answering | null;
  capabilities?: TargetCapabilities;
  apps?: RecordedMessage["apps"];

  agent?: string | null;
  how?: string;
  why?: string;
  period?: string | null;

  state?: string;
  startedAt?: string | null;
  endedAt?: string | null;
};

export type Wrote = {
  key: string;
  id: string | null;
  by: string;
  text: string;
  at: string;

  submission: string;

  from: Origin | null;

  answering?: Answering | null;
};

export type Stop = { agent: string | null; how: string; why: string; period: string | null };

export type CallOnALine = {
  call: string;
  live: boolean;
  startedAt: string | null;
  endedAt: string | null;
  reactions: Reaction[];
};

export type Row = {
  key: string;
  by: string;
  text: string;
  at: string;
  from: Origin | null;

  stop?: Stop;

  call?: CallOnALine;

  between?: boolean;

  id: string | null;
  reactions: Reaction[];
  away: PutAway;
  answering: Answering | null;
  capabilities: TargetCapabilities;
  apps?: RecordedMessage["apps"];
};

export function rowsOf(read: readonly Line[], wrote: readonly Wrote[]): Row[] {
  const byId = new Map<string, Wrote>();
  const bySubmission = new Map<string, Wrote>();
  for (const one of wrote) {
    if (one.id) byId.set(one.id, one);
    bySubmission.set(one.submission, one);
  }
  const landed = new Set<string>();
  const rows: Row[] = read.map((one) => {
    const held = byId.get(one.id) ?? (one.submission ? bySubmission.get(one.submission) : undefined);
    if (held) landed.add(held.key);

    if (one.kind === "call") {
      return {
        key: one.id,
        by: "",
        text: "",
        at: one.at,
        from: null,
        id: one.id,
        reactions: [],
        away: NOTHING_PUT_AWAY,
        answering: null,
        capabilities: ALL_TARGET_CAPABILITIES,
        call: {
          call: one.id,
          live: one.state === "live",
          startedAt: one.startedAt ?? null,
          endedAt: one.endedAt ?? null,
          reactions: one.reactions ?? [],
        },
      };
    }

    if (one.kind === "stopped") {
      return {
        key: one.id,
        by: one.by ?? "",
        text: "",
        at: one.at,
        from: null,
        id: one.id,
        reactions: [],
        away: NOTHING_PUT_AWAY,
        answering: null,
        capabilities: ALL_TARGET_CAPABILITIES,
        stop: {
          agent: one.agent ?? null,
          how: one.how ?? "broke",
          why: one.why ?? "",
          period: one.period ?? null,
        },
      };
    }

    return {
      key: held?.key ?? one.id,
      by: one.by ?? "",
      text: one.text ?? "",
      at: one.at,
      from: held?.from ?? null,
      id: one.id,
      reactions: one.reactions ?? [],
      away: one.away ?? NOTHING_PUT_AWAY,
      answering: one.answering ?? null,
      capabilities: one.capabilities ?? ALL_TARGET_CAPABILITIES,
      ...(one.apps?.length ? { apps: one.apps } : {}),
      ...(one.between ? { between: true } : {}),
    };
  });
  for (const one of wrote) {
    if (landed.has(one.key)) continue;

    rows.push({
      key: one.key,
      by: one.by,
      text: one.text,
      at: one.at,
      from: one.from,
      id: one.id,
      reactions: [],
      away: NOTHING_PUT_AWAY,
      answering: one.answering ?? null,
      capabilities: ALL_TARGET_CAPABILITIES,
    });
  }
  return rows;
}
